import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Pause, Play, Download, Plus, Pencil, Copy, Trash2, ExternalLink, Square, X } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { AffiliateCampaignForm } from "../components/AffiliateCampaignForm";
import { UsersManager, ContentManager } from "../components/AdminManagement";

const TABS = ["overview", "users", "featured", "articles", "manage-articles", "shorts", "manage-shorts", "comments", "spam", "campaigns", "reports", "affiliate", "publishing", "rss", "channels", "newsletter", "settings", "logs"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState({});
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [affiliate, setAffiliate] = useState([]);
  const [heatmap, setHeatmap] = useState({ by_page: [], by_placement: [] });
  const [reports, setReports] = useState([]);
  const [capInput, setCapInput] = useState(0);
  const [retentionInput, setRetentionInput] = useState(0);
  const [heatSince, setHeatSince] = useState("");
  const [heatUntil, setHeatUntil] = useState("");
  const [carSel, setCarSel] = useState([]);
  const [carCand, setCarCand] = useState([]);
  const [spam, setSpam] = useState({ flagged: [], caught: [] });
  const [restricted, setRestricted] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [onePerPage, setOnePerPage] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [social, setSocial] = useState({ linkedin: "", youtube: "" });
  const [commentPreview, setCommentPreview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [channels, setChannels] = useState([]);
  const [chanInput, setChanInput] = useState("");
  const [chanName, setChanName] = useState("");

  const loadChannels = () => api.get("/admin/curated-channels").then((r) => setChannels(r.data || [])).catch(() => {});
  const extractChannelId = (raw) => { const m = (raw || "").match(/UC[\w-]{20,}/); return m ? m[0] : (raw || "").trim(); };
  const addChannel = async () => {
    const cid = extractChannelId(chanInput);
    if (!cid.startsWith("UC")) { toast.error("Paste a valid Channel ID (starts with UC…) or a youtube.com/channel/UC… URL. @handle links aren't supported."); return; }
    try { await api.post("/admin/curated-channels", { channel_id: cid, name: chanName.trim() }); toast.success("Channel added — Shorts feed will refresh"); setChanInput(""); setChanName(""); loadChannels(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delChannel = async (cid) => { if (!window.confirm("Remove this channel from the curated Shorts feed?")) return; try { await api.delete(`/admin/curated-channels/${cid}`); toast.success("Channel removed"); loadChannels(); } catch (e) { toast.error(apiErr(e)); } };

  const [pubOrders, setPubOrders] = useState([]);
  const [pubPkgs, setPubPkgs] = useState([]);
  const [priceDraft, setPriceDraft] = useState({});
  const loadPublishing = () => {
    api.get("/admin/publishing/orders").then((r) => setPubOrders(r.data || [])).catch(() => {});
    api.get("/admin/publishing/packages").then((r) => { setPubPkgs(r.data || []); setPriceDraft(Object.fromEntries((r.data || []).map((p) => [p.lookup_key, p.price]))); }).catch(() => {});
  };
  const pubModerate = async (id, status) => {
    const note = (status === "Needs Revision" || status === "Rejected") ? (window.prompt(`Add a note to the author (${status})`, "") ?? "") : "";
    try { await api.post(`/admin/moderate/articles/${id}`, { status, note }); toast.success(status); loadPublishing(); } catch (e) { toast.error(apiErr(e)); }
  };
  const pubRefund = async (orderId) => { if (!window.confirm("Refund this publishing payment in full?")) return; try { await api.post(`/admin/publishing/orders/${orderId}/refund`); toast.success("Refunded"); loadPublishing(); } catch (e) { toast.error(apiErr(e)); } };
  const savePubPrices = async () => { try { await api.post("/admin/publishing/packages/prices", { prices: priceDraft }); toast.success("Publishing prices saved"); loadPublishing(); } catch (e) { toast.error(apiErr(e)); } };

  const loadSpam = () => {
    api.get("/admin/spam").then((r) => setSpam(r.data)).catch(() => {});
    api.get("/admin/restricted").then((r) => setRestricted(r.data)).catch(() => {});
  };
  const setRestrict = async (id, value) => { try { await api.post(`/admin/users/${id}/restrict?value=${value}`); toast.success(value ? "User restricted" : "User un-restricted"); loadSpam(); loadOverview(); } catch (e) { toast.error(apiErr(e)); } };

  const loadCarousel = () => api.get("/admin/carousel").then((r) => { setCarSel(r.data.selected); setCarCand(r.data.candidates); }).catch(() => {});

  const loadOverview = () => api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
  const loadQueue = (kind) => api.get(`/admin/queue?kind=${kind}`).then((r) => setItems(r.data)).catch(() => {});
  const loadHeatmap = (since = heatSince, until = heatUntil) => {
    const qs = [];
    if (since) qs.push(`since=${since}`);
    if (until) qs.push(`until=${until}`);
    api.get(`/admin/affiliate/heatmap${qs.length ? "?" + qs.join("&") : ""}`).then((r) => setHeatmap(r.data)).catch(() => {});
  };
  const loadAffiliate = () => {
    api.get("/admin/affiliate").then((r) => setAffiliate(r.data)).catch(() => {});
    loadHeatmap();
    api.get("/admin/affiliate/placements").then((r) => setPlacements(r.data)).catch(() => {});
    api.get("/admin/affiliate/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/affiliate/settings").then((r) => { setCapInput(r.data.frequency_cap || 0); setRetentionInput(r.data.report_retention_months || 0); setOnePerPage(r.data.one_per_page !== false); }).catch(() => {});
  };

  useEffect(() => { loadOverview(); }, []);
  useEffect(() => {
    if (tab === "logs") api.get("/admin/logs").then((r) => setLogs(r.data)).catch(() => {});
    else if (tab === "affiliate") loadAffiliate();
    else if (tab === "featured") loadCarousel();
    else if (tab === "spam") loadSpam();
    else if (tab === "settings") api.get("/settings/social").then((r) => setSocial(r.data || { linkedin: "", youtube: "" })).catch(() => {});
    else if (tab === "newsletter") api.get("/admin/newsletter").then((r) => setSubs(r.data.subscribers || [])).catch(() => {});
    else if (tab === "channels") loadChannels();
    else if (tab === "publishing") loadPublishing();
    else if (tab !== "overview") loadQueue(tab);
  }, [tab]);

  const refresh = () => { loadOverview(); if (tab === "spam") loadSpam(); else if (tab !== "overview" && tab !== "logs") loadQueue(tab); };

  const moderate = async (kind, id, status, note = "") => {
    try { await api.post(`/admin/moderate/${kind}/${id}`, { status, note }); toast.success(`${status}`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const feature = async (id) => { try { const { data } = await api.post(`/admin/articles/${id}/feature`); toast.success(data.featured ? "Featured" : "Unfeatured"); } catch (e) { toast.error(apiErr(e)); } };
  const rssAction = async (id, action) => { try { await api.post(`/admin/rss/${id}/action?action=${action}`); toast.success(action); loadQueue("rss"); } catch (e) { toast.error(apiErr(e)); } };
  const rssRefresh = async () => { try { const { data } = await api.post("/admin/rss/refresh"); toast.success(`Refreshed — ${data.count} items`); loadQueue("rss"); } catch (e) { toast.error(apiErr(e)); } };
  const pauseWire = async (p) => { try { await api.post(`/admin/inkwire/pause?paused=${p}`); toast.success(p ? "Wire paused" : "Wire resumed"); } catch (e) { toast.error(apiErr(e)); } };
  const refund = async (id) => { try { await api.post(`/admin/campaigns/${id}/refund`); toast.success("Refunded"); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/affiliate/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "affiliate_report.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveCap = async () => { try { await api.post("/admin/affiliate/settings", { frequency_cap: Number(capInput), report_retention_months: Number(retentionInput), one_per_page: onePerPage }); toast.success("Settings saved"); loadAffiliate(); } catch (e) { toast.error(apiErr(e)); } };
  const saveSocial = async () => { try { await api.post("/admin/settings/social", { linkedin: (social.linkedin || "").trim(), youtube: (social.youtube || "").trim() }); toast.success("Social links saved"); } catch (e) { toast.error(apiErr(e)); } };
  const exportSubs = async () => {
    try {
      const res = await api.get("/admin/newsletter/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "newsletter_subscribers.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const previewDigest = async () => {
    try {
      const res = await api.get("/admin/newsletter/digest-preview");
      if (!res.data.articles) { toast.error("No published articles to feature yet."); return; }
      setDigestPreview(res.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const sendDigest = async () => {
    if (digestBusy) return;
    if (!window.confirm("Send the weekly digest (top recent articles) to all subscribers now?")) return;
    setDigestBusy(true);
    try {
      const res = await api.post("/admin/newsletter/send-digest");
      const d = res.data || {};
      if (d.error) toast.error(d.error);
      else toast.success(`Digest sent to ${d.sent} subscriber${d.sent === 1 ? "" : "s"} (${d.articles} articles).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setDigestBusy(false); }
  };
  const setStatusAff = async (id, status) => { try { await api.post(`/admin/affiliate/${id}/status?status=${status}`); toast.success(`Campaign ${status}`); loadAffiliate(); } catch (e) { toast.error(apiErr(e)); } };
  const duplicateAff = async (id) => { try { await api.post(`/admin/affiliate/${id}/duplicate`); toast.success("Campaign duplicated (Draft)"); loadAffiliate(); } catch (e) { toast.error(apiErr(e)); } };
  const deleteAff = async (id) => { if (!window.confirm("Permanently delete this campaign and its history?")) return; try { await api.delete(`/admin/affiliate/${id}`); toast.success("Campaign deleted"); loadAffiliate(); } catch (e) { toast.error(apiErr(e)); } };
  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (ad) => { setEditing(ad); setShowForm(true); };
  const viewLive = (ad) => { const p = placements.find((x) => ad.placements?.includes(x.key)); window.open(p ? p.url : "/", "_blank"); };
  const genReport = async () => { try { await api.post("/admin/affiliate/reports/generate"); toast.success("Weekly report generated"); loadAffiliate(); } catch (e) { toast.error(apiErr(e)); } };
  const downloadReport = async (id, filename) => {
    try {
      const res = await api.get(`/admin/affiliate/reports/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveCarousel = async (sel) => { try { await api.post("/admin/carousel", { ids: sel.map((s) => s.id) }); toast.success("Featured carousel saved"); } catch (e) { toast.error(apiErr(e)); } };
  const addCar = (id) => { if (carSel.length >= 3 || carSel.find((s) => s.id === id)) return; const c = carCand.find((x) => x.id === id); if (!c) return; const next = [...carSel, c]; setCarSel(next); saveCarousel(next); };
  const removeCar = (id) => { const next = carSel.filter((s) => s.id !== id); setCarSel(next); saveCarousel(next); };
  const moveCar = (i, d) => { const j = i + d; if (j < 0 || j >= carSel.length) return; const next = [...carSel]; [next[i], next[j]] = [next[j], next[i]]; setCarSel(next); saveCarousel(next); };

  return (
    <div className="pti-container py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-4xl font-black">Admin</h1>
        <div className="flex items-center gap-4">
          <a href="/admin/daily3" data-testid="admin-daily3-link" className="flex items-center gap-1.5 text-sm font-bold bg-pti-black text-white px-4 py-2 hover:bg-pti-red transition-colors">Daily 3 Editorial Desk</a>
          <button onClick={refresh} className="flex items-center gap-2 text-sm font-bold text-pti-red"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="flex gap-4 mt-5 border-b border-gray-200 overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} role="tab" aria-selected={tab === t} data-testid={`admin-tab-${t}`}
            className={`text-sm font-bold uppercase tracking-wider pb-2 border-b-2 whitespace-nowrap transition-colors ${tab === t ? "border-pti-red text-pti-black" : "border-transparent text-gray-500"}`}>{({ "articles": "Article Reviews", "shorts": "Short Reviews", "manage-articles": "Manage Articles", "manage-shorts": "Manage Shorts", "settings": "Settings", "newsletter": "Subscribers", "channels": "Curated Channels", "publishing": "Publishing" }[t]) || t}</button>
        ))}
      </div>

      {tab === "users" && <UsersManager />}
      {tab === "manage-articles" && <ContentManager kind="articles" />}
      {tab === "manage-shorts" && <ContentManager kind="shorts" />}

      {tab === "newsletter" && (
        <div className="mt-6" data-testid="admin-newsletter">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-display text-xl font-black">Newsletter Subscribers</h2>
              <p className="text-sm text-gray-500 mt-1" data-testid="subs-count">{subs.length} subscriber{subs.length === 1 ? "" : "s"}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={exportSubs} disabled={subs.length === 0} data-testid="subs-export-csv" className="flex items-center gap-2 bg-pti-black text-white px-4 py-2 text-sm font-bold hover:bg-pti-red transition-colors disabled:opacity-50"><Download size={14} /> Export CSV</button>
              <button onClick={previewDigest} data-testid="subs-preview-digest" className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-sm font-bold hover:border-pti-black transition-colors">Preview digest</button>
              <button onClick={sendDigest} disabled={subs.length === 0 || digestBusy} data-testid="subs-send-digest" className="flex items-center gap-2 border border-pti-red text-pti-red px-4 py-2 text-sm font-bold hover:bg-pti-red hover:text-white transition-colors disabled:opacity-50">{digestBusy ? "Sending…" : "Send weekly digest"}</button>
            </div>
          </div>
          {subs.length === 0 ? (
            <p className="text-gray-400 py-16 text-center border border-dashed border-gray-200">No subscribers yet.</p>
          ) : (
            <div className="border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-bold uppercase tracking-wider text-gray-500">
                  <tr><th className="px-4 py-2 w-12">#</th><th className="px-4 py-2">Email</th><th className="px-4 py-2 whitespace-nowrap">Subscribed</th><th className="px-4 py-2 text-right">Unsubscribe</th></tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {subs.map((s, i) => (
                    <tr key={s.email} data-testid={`sub-row-${i}`} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                      <td className="px-4 py-2 font-medium">{s.email}</td>
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{s.created_at ? new Date(s.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <button data-testid={`sub-copy-${i}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/unsubscribe?token=${s.unsubscribe_token}`); toast.success("Unsubscribe link copied"); }}
                          className="text-xs font-bold text-pti-red hover:underline whitespace-nowrap">Copy link</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {digestPreview && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(17,17,17,0.6)" }} onClick={() => setDigestPreview(null)} data-testid="digest-preview-modal">
              <div className="bg-white w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(ev) => ev.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                  <div>
                    <h3 className="font-display text-lg font-black">Weekly Digest Preview</h3>
                    <p className="text-xs text-gray-500">{digestPreview.articles} article{digestPreview.articles === 1 ? "" : "s"} · will send to {digestPreview.subscribers} subscriber{digestPreview.subscribers === 1 ? "" : "s"}</p>
                  </div>
                  <button onClick={() => setDigestPreview(null)} data-testid="digest-preview-close" className="text-gray-400 hover:text-pti-black"><X size={20} /></button>
                </div>
                <iframe title="Digest preview" srcDoc={digestPreview.html} data-testid="digest-preview-frame" className="flex-1 w-full border-0" style={{ minHeight: "60vh" }} />
                <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
                  <button onClick={() => setDigestPreview(null)} className="px-4 py-2 text-sm font-bold border border-gray-300 hover:border-pti-black">Close</button>
                  <button onClick={() => { setDigestPreview(null); sendDigest(); }} disabled={digestPreview.subscribers === 0} data-testid="digest-preview-send" className="px-5 py-2 text-sm font-bold bg-pti-red text-white hover:bg-pti-black transition-colors disabled:opacity-50">Send to all subscribers</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && (
        <div className="mt-6 max-w-xl" data-testid="admin-settings">
          <h2 className="font-display text-xl font-black">Social Links</h2>
          <p className="text-sm text-gray-500 mt-1 mb-5">Official PictureThisInk profiles shown in the site footer. Update these anytime — no code changes needed. Leave a field blank to hide that icon.</p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold block mb-1">LinkedIn URL</label>
              <input value={social.linkedin || ""} onChange={(e) => setSocial((s) => ({ ...s, linkedin: e.target.value }))} data-testid="settings-linkedin"
                placeholder="https://www.linkedin.com/company/picturethisink" className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-pti-red outline-none" />
            </div>
            <div>
              <label className="text-sm font-bold block mb-1">YouTube URL</label>
              <input value={social.youtube || ""} onChange={(e) => setSocial((s) => ({ ...s, youtube: e.target.value }))} data-testid="settings-youtube"
                placeholder="https://www.youtube.com/@picturethisink" className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-pti-red outline-none" />
            </div>
            <button onClick={saveSocial} data-testid="settings-social-save" className="bg-pti-red text-white text-sm font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-pti-redhover transition-colors">Save social links</button>
          </div>
        </div>
      )}

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {[["Pending Articles", overview.pending_articles], ["Pending Shorts", overview.pending_shorts], ["Pending Comments", overview.pending_comments], ["Flagged for Review", overview.flagged_items], ["Caught Spam", overview.caught_spam], ["Restricted Users", overview.restricted_users], ["Pending Ads", overview.pending_campaigns], ["Open Reports", overview.open_reports], ["Total Users", overview.total_users], ["Creators", overview.total_creators], ["Live Articles", overview.total_articles], ["Subscribers", overview.total_subscribers], ["New Subs (7d)", overview.new_subscribers_7d]].map(([l, v]) => (
            <div key={l} data-testid={`stat-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`} className={`border p-5 ${(l === "Flagged for Review" || l === "Restricted Users") && v > 0 ? "border-pti-red bg-red-50" : l === "New Subs (7d)" && v > 0 ? "border-pti-red bg-red-50" : "border-gray-200"}`}><div className="font-display text-3xl font-bold">{v ?? 0}</div><div className="text-xs uppercase tracking-wider text-gray-500">{l}</div></div>
          ))}
        </div>
      )}

      {tab === "featured" && (
        <div className="mt-6 max-w-2xl" data-testid="admin-featured">
          <p className="text-sm text-gray-600 mb-3">Choose up to 3 stories for the homepage Featured carousel. Reorder or remove them — changes save instantly.</p>
          <div className="space-y-2">
            {carSel.length === 0 && <p className="text-gray-400 text-sm">No stories selected — the carousel falls back to the newest featured articles.</p>}
            {carSel.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between border border-gray-200 p-3" data-testid={`car-sel-${s.id}`}>
                <div className="min-w-0"><span className="font-display font-bold mr-2">{i + 1}.</span><span className="font-semibold">{s.title}</span> <span className="text-xs text-pti-red">{s.category}</span></div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => moveCar(i, -1)} data-testid={`car-up-${s.id}`} aria-label="Move up" className="px-2 py-1 border border-gray-300 text-xs hover:border-pti-black">↑</button>
                  <button onClick={() => moveCar(i, 1)} data-testid={`car-down-${s.id}`} aria-label="Move down" className="px-2 py-1 border border-gray-300 text-xs hover:border-pti-black">↓</button>
                  <button onClick={() => removeCar(s.id)} data-testid={`car-remove-${s.id}`} className="px-2 py-1 border border-red-300 text-red-600 text-xs hover:bg-red-50">Remove</button>
                </div>
              </div>
            ))}
          </div>
          {carSel.length < 3 && (
            <div className="mt-4">
              <label className="text-sm font-bold">Add a story</label>
              <select onChange={(e) => { if (e.target.value) { addCar(e.target.value); e.target.value = ""; } }} data-testid="car-add" className="w-full border border-gray-300 px-3 py-2.5 mt-1 outline-none focus:border-pti-red">
                <option value="">Select an article…</option>
                {carCand.filter((c) => !carSel.find((s) => s.id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.title} ({c.category})</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {(tab === "articles" || tab === "shorts") && (
        <>
        <p className="mt-6 text-sm text-gray-600 border-l-4 border-pti-red pl-3" data-testid="review-queue-hint">This is the <strong>review queue</strong> — only {tab === "articles" ? "articles" : "Shorts"} awaiting approval appear here. To see, create, or edit <strong>all {tab === "articles" ? "articles" : "Shorts"}</strong> (published, drafts, archived), open the <button onClick={() => setTab(tab === "articles" ? "manage-articles" : "manage-shorts")} className="font-bold text-pti-red underline">{tab === "articles" ? "Manage Articles" : "Manage Shorts"}</button> tab.</p>
        <Queue items={items} empty="Nothing awaiting review right now." render={(it) => (
          <Row key={it.id} title={it.title} sub={`${it.category} · @${it.author_username}`} badge={it.spam_flag ? it.spam_reason : null}>
            <Act onClick={() => moderate(tab, it.id, "Approved")} label="Approve" primary />
            <Act onClick={() => moderate(tab, it.id, "Needs Revision", "Please revise.")} label="Needs Revision" />
            <Act onClick={() => moderate(tab, it.id, "Rejected")} label="Reject" danger />
            {tab === "articles" && <Act onClick={() => feature(it.id)} label="Feature" />}
          </Row>
        )} />
        </>
      )}

      {tab === "spam" && (
        <div className="mt-6 space-y-8" data-testid="admin-spam">
          <p className="text-sm text-gray-600">Obvious spam is auto-blocked and listed under <strong>Caught spam</strong>. Borderline content is auto-flagged and waits here under <strong>Flagged for review</strong> — approve to publish or remove it. Creators are auto-restricted after <strong>3 caught-spam strikes</strong>.</p>

          <div data-testid="spam-restricted">
            <h2 className="font-display text-xl font-bold border-b-2 border-pti-black pb-1 mb-3">Repeat offenders & restricted creators ({restricted.length})</h2>
            {restricted.length === 0 ? <p className="text-gray-400 text-sm">No creators have spam strikes yet.</p> : (
              <div className="border border-gray-200 divide-y divide-gray-100">
                {restricted.map((u) => (
                  <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`restricted-${u.id}`}>
                    <div className="min-w-0">
                      <div className="font-bold flex items-center gap-2">{u.display_name} <span className="text-xs text-gray-400">@{u.username}</span>
                        {u.restricted && <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border border-red-300 bg-red-50 text-pti-red">Restricted</span>}
                      </div>
                      <div className="text-xs text-gray-500">{u.email} · {u.spam_strikes} strike(s)</div>
                    </div>
                    {u.restricted
                      ? <ActionBtn onClick={() => setRestrict(u.id, false)} testid={`unrestrict-${u.id}`} icon={<Play size={13} />} label="Un-restrict" tone="green" />
                      : <ActionBtn onClick={() => setRestrict(u.id, true)} testid={`restrict-${u.id}`} icon={<Square size={12} />} label="Restrict" tone="red" />}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div data-testid="spam-flagged">
            <h2 className="font-display text-xl font-bold border-b-2 border-pti-red pb-1 mb-3">Flagged for review ({spam.flagged.length})</h2>
            {spam.flagged.length === 0 ? <p className="text-gray-400 text-sm">Nothing flagged right now.</p> : spam.flagged.map((it) => (
              <div key={it.id} className="border border-gray-200 p-4 mb-3" data-testid={`spam-flagged-${it.id}`}>
                <Row title={it.title || it.body} sub={`${it._type} · @${it.author_username}`} badge={it.spam_reason || "flagged"}>
                  <Act onClick={() => moderate(it._type, it.id, "Approved")} label="Approve" primary />
                  <Act onClick={() => moderate(it._type, it.id, it._type === "comments" ? "Rejected" : "Removed")} label="Remove" danger />
                </Row>
              </div>
            ))}
          </div>

          <div data-testid="spam-caught">
            <h2 className="font-display text-xl font-bold border-b-2 border-pti-black pb-1 mb-3">Caught spam ({spam.caught.length})</h2>
            {spam.caught.length === 0 ? <p className="text-gray-400 text-sm">No spam has been auto-blocked yet.</p> : spam.caught.map((it) => (
              <div key={it.id} className="border border-gray-200 bg-gray-50 p-4 mb-3" data-testid={`spam-caught-${it.id}`}>
                <Row title={it.title || it.body} sub={`${it._type} · @${it.author_username}`} badge={it.spam_reason || "auto-blocked"}>
                  <Act onClick={() => moderate(it._type, it.id, "Approved")} label="Not spam — publish" primary />
                  <Act onClick={() => moderate(it._type, it.id, it._type === "comments" ? "Rejected" : "Removed")} label="Delete" danger />
                </Row>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "comments" && (
        <Queue items={items} empty="No comments awaiting review." render={(it) => (
          <Row key={it.id} title={it.body} sub={`@${it.author_username}${it.content_title ? ` · on “${it.content_title}”` : ""}`} badge={it.spam_flag ? it.spam_reason : null}>
            <Act onClick={() => setCommentPreview(it)} label="Preview" />
            <Act onClick={() => moderate("comments", it.id, "Approved")} label="Approve" primary />
            <Act onClick={() => moderate("comments", it.id, "Rejected")} label="Reject" danger />
          </Row>
        )} />
      )}

      {tab === "campaigns" && (
        <Queue items={items} empty="No ads awaiting approval." render={(it) => (
          <Row key={it.id} title={it.headline} sub={`${it.brand_name} · ${it.package} · $${it.price} · ${it.payment_status}`}>
            <Act onClick={() => moderate("campaigns", it.id, "Approved")} label="Approve & schedule" primary />
            <Act onClick={() => moderate("campaigns", it.id, "Needs Revision")} label="Request revision" />
            <Act onClick={() => moderate("campaigns", it.id, "Rejected")} label="Reject" danger />
            {it.payment_status === "paid" && <Act onClick={() => refund(it.id)} label="Refund" />}
          </Row>
        )} />
      )}

      {tab === "reports" && (
        <Queue items={items} empty="No open reports." render={(it) => (
          <Row key={it.id} title={`${it.reason} — ${it.content_type}`} sub={`${it.details || "No details"} · by ${it.reporter}`}>
            <span className="text-xs text-gray-400">{it.content_id?.slice(0, 8)}</span>
          </Row>
        )} />
      )}

      {tab === "rss" && (
        <div className="mt-6">
          <div className="flex flex-wrap gap-2 mb-4">
            <button onClick={rssRefresh} data-testid="rss-refresh" className="flex items-center gap-2 bg-pti-black text-white px-4 py-2 text-sm font-bold"><RefreshCw size={14} /> Refresh feeds</button>
            <button onClick={() => pauseWire(true)} className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-sm font-bold"><Pause size={14} /> Pause live bar</button>
            <button onClick={() => pauseWire(false)} className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-sm font-bold"><Play size={14} /> Resume</button>
          </div>
          <Queue items={items} empty="No RSS items." render={(it) => (
            <Row key={it.id} title={it.title} sub={`${it.category} · ${it.source}${it.pinned ? " · 📌 Pinned" : ""}${it.hidden ? " · Hidden" : ""}`}>
              <Act onClick={() => rssAction(it.id, it.pinned ? "unpin" : "pin")} label={it.pinned ? "Unpin" : "Pin"} />
              <Act onClick={() => rssAction(it.id, it.hidden ? "show" : "hide")} label={it.hidden ? "Show" : "Hide"} />
              <Act onClick={() => rssAction(it.id, it.featured ? "unfeature" : "feature")} label={it.featured ? "Unfeature" : "Feature"} />
            </Row>
          )} />
        </div>
      )}

      {tab === "publishing" && (
        <div className="mt-6 space-y-8" data-testid="admin-publishing">
          <div>
            <h2 className="font-display text-2xl font-bold">Publishing Orders</h2>
            <p className="text-sm text-gray-500">Paid article submissions. Approve, request revisions, reject, or refund. Approved articles publish live. Submissions also appear in the Article Reviews tab.</p>
          </div>

          {/* Price manager */}
          <div className="border border-gray-200 p-5" data-testid="pub-price-manager">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Publishing prices</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {pubPkgs.map((p) => (
                <div key={p.lookup_key} data-testid={`pub-price-${p.lookup_key}`}>
                  <label className="text-xs font-bold">{p.name}</label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-gray-400 font-bold">$</span>
                    <input type="number" min="0" step="1" value={priceDraft[p.lookup_key] ?? ""} onChange={(e) => setPriceDraft({ ...priceDraft, [p.lookup_key]: e.target.value })} data-testid={`pub-price-input-${p.lookup_key}`} className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-pti-red" />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={savePubPrices} data-testid="pub-save-prices" className="mt-4 bg-pti-black text-white px-5 py-2 text-sm font-bold hover:bg-pti-red transition-colors">Save prices</button>
          </div>

          {/* Orders */}
          {pubOrders.length === 0 && <p className="text-gray-400">No publishing orders yet.</p>}
          <div className="space-y-5">
            {pubOrders.map((o) => (
              <div key={o.id} className="border border-gray-200" data-testid={`pub-order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-3 border-b border-gray-200 bg-gray-50">
                  <div>
                    <div className="font-bold">{o.package_name} <span className="text-gray-400 font-normal">· @{o.username}</span></div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      ${o.amount} · <span className={`font-bold ${o.payment_status === "paid" ? "text-green-700" : o.payment_status === "refunded" ? "text-red-600" : "text-gray-500"}`}>{o.payment_status}</span>
                      {(o.credits_total || 1) > 1 && <> · {o.credits_remaining}/{o.credits_total} left</>}
                    </div>
                  </div>
                  {o.payment_status === "paid" && (
                    <button onClick={() => pubRefund(o.id)} data-testid={`pub-refund-${o.id}`} className="text-xs font-bold border border-gray-300 px-3 py-1.5 hover:border-pti-red hover:text-pti-red transition-colors">Refund</button>
                  )}
                </div>
                <div className="divide-y divide-gray-100">
                  {(o.articles || []).length === 0 && <div className="px-5 py-3 text-sm text-gray-400">No article submitted on this order yet.</div>}
                  {(o.articles || []).map((a) => (
                    <div key={a.id} className="px-5 py-3" data-testid={`pub-article-${a.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="min-w-0">
                          <p className="font-bold text-sm truncate">{a.title} {a.sponsored && <span className="ml-1 bg-pti-black text-white text-[10px] px-1.5 py-0.5 align-middle">SPONSORED</span>}</p>
                          <p className="text-xs text-gray-400">{a.category} · status: <span className="font-bold">{a.status}</span></p>
                          {a.publishing_website && <a href={a.publishing_website} target="_blank" rel="noreferrer" className="text-xs text-pti-red">{a.publishing_website}</a>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {a.status === "approved"
                            ? <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="text-xs font-bold text-pti-red">View live</a>
                            : a.status === "payment_pending"
                              ? <span className="text-xs text-gray-400">Awaiting payment</span>
                              : <>
                                <button onClick={() => pubModerate(a.id, "Approved")} data-testid={`pub-approve-${a.id}`} className="text-xs font-bold bg-green-600 text-white px-3 py-1.5 hover:bg-green-700 transition-colors">Approve</button>
                                <button onClick={() => pubModerate(a.id, "Needs Revision")} data-testid={`pub-revise-${a.id}`} className="text-xs font-bold border border-gray-300 px-3 py-1.5 hover:border-pti-black transition-colors">Revise</button>
                                <button onClick={() => pubModerate(a.id, "Rejected")} data-testid={`pub-reject-${a.id}`} className="text-xs font-bold border border-gray-300 px-3 py-1.5 hover:border-pti-red hover:text-pti-red transition-colors">Reject</button>
                              </>}
                        </div>
                      </div>
                      {(a.publishing_links || []).length > 0 && (
                        <div className="mt-2 text-xs text-gray-500">Links: {a.publishing_links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="text-pti-red mr-2">{l.label || l.url}</a>)}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "channels" && (
        <div className="mt-6 max-w-3xl" data-testid="admin-channels">
          <h2 className="font-display text-2xl font-bold">Curated YouTube Channels</h2>
          <p className="text-sm text-gray-500 mt-1">Approved channels feed the Curated Tech Shorts. Add or remove them here — no code required. Changes refresh the Shorts feed automatically.</p>

          <div className="mt-6 border border-gray-200 p-5">
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Channel ID or Channel URL</label>
            <input value={chanInput} onChange={(e) => setChanInput(e.target.value)} data-testid="channel-id-input" placeholder="UCxxxxxxxxxxxxxxxxxxxxxx or https://youtube.com/channel/UC…" className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-pti-red" />
            <p className="text-xs text-gray-400 mt-1">Paste the raw Channel ID (starts with <code>UC</code>) or a full <code>/channel/UC…</code> URL. Note: <code>@handle</code> links can't be auto-resolved.</p>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1 mt-4">Display name (optional)</label>
            <input value={chanName} onChange={(e) => setChanName(e.target.value)} data-testid="channel-name-input" placeholder="e.g. Marques Brownlee" className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-pti-red" />
            <button onClick={addChannel} data-testid="add-channel-btn" className="mt-4 flex items-center gap-2 bg-pti-red text-white px-5 py-2.5 text-sm font-bold hover:bg-pti-black transition-colors"><Plus size={16} /> Add Channel</button>
          </div>

          <div className="mt-8">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-500 mb-3">Approved channels ({channels.length})</h3>
            {channels.length === 0 && <p className="text-gray-400">No channels configured yet.</p>}
            <div className="divide-y divide-gray-200 border border-gray-200">
              {channels.map((c) => (
                <div key={c.channel_id} className="flex items-center justify-between px-4 py-3" data-testid={`channel-row-${c.channel_id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-sm truncate">{c.name || c.channel_id}</p>
                    <p className="text-xs text-gray-400 font-mono truncate">{c.channel_id}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <a href={`https://www.youtube.com/channel/${c.channel_id}`} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-pti-black" title="Open on YouTube"><ExternalLink size={16} /></a>
                    <button onClick={() => delChannel(c.channel_id)} data-testid={`delete-channel-${c.channel_id}`} className="text-gray-400 hover:text-pti-red" title="Remove"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "affiliate" && (
        <div className="mt-6 space-y-8" data-testid="admin-affiliate">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold">Affiliate Ad Manager</h2>
              <p className="text-sm text-gray-500">PictureThisInk-owned campaigns (Verizon, NordVPN, GameSeal & more). Paid advertiser submissions live under the Campaigns tab.</p>
            </div>
            <button onClick={openCreate} data-testid="create-affiliate-campaign" className="flex items-center gap-2 bg-pti-red text-white px-5 py-2.5 text-sm font-bold hover:bg-pti-black transition-colors"><Plus size={16} /> Create Affiliate Campaign</button>
          </div>

          {/* Controls: frequency cap, retention, one-per-page, export, generate weekly report */}
          <div className="flex flex-wrap items-end gap-4 border border-gray-200 p-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block">Frequency cap (per session, 0 = unlimited)</label>
              <input type="number" min="0" value={capInput} onChange={(e) => setCapInput(e.target.value)} data-testid="freq-cap-input" className="w-24 border border-gray-300 px-2 py-1.5 text-sm mt-1 outline-none focus:border-pti-red" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block">Report retention (months, 0 = keep forever)</label>
              <input type="number" min="0" value={retentionInput} onChange={(e) => setRetentionInput(e.target.value)} data-testid="retention-input" className="w-24 border border-gray-300 px-2 py-1.5 text-sm mt-1 outline-none focus:border-pti-red" />
            </div>
            <label className="flex items-center gap-2 text-sm font-bold cursor-pointer pb-1.5" data-testid="one-per-page-toggle">
              <input type="checkbox" checked={onePerPage} onChange={(e) => setOnePerPage(e.target.checked)} className="accent-pti-red w-4 h-4" />
              One affiliate ad per page view
            </label>
            <button onClick={saveCap} data-testid="freq-cap-save" className="text-xs font-bold px-3 py-2 border border-pti-black hover:bg-pti-black hover:text-white transition-colors">Save settings</button>
            <div className="flex gap-2 ml-auto">
              <button onClick={exportCsv} data-testid="affiliate-export-csv" className="flex items-center gap-2 bg-pti-black text-white px-4 py-2 text-sm font-bold hover:bg-pti-red transition-colors"><Download size={14} /> Export CSV</button>
              <button onClick={genReport} data-testid="generate-report" className="flex items-center gap-2 border border-pti-black px-4 py-2 text-sm font-bold hover:bg-gray-100 transition-colors"><RefreshCw size={14} /> Generate weekly report</button>
            </div>
          </div>

          {/* Click heatmap with date-range filter */}
          <div data-testid="affiliate-heatmap">
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block">From</label>
                <input type="date" value={heatSince} onChange={(e) => setHeatSince(e.target.value)} data-testid="heatmap-since" className="border border-gray-300 px-2 py-1.5 text-sm mt-1 outline-none focus:border-pti-red" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block">To</label>
                <input type="date" value={heatUntil} onChange={(e) => setHeatUntil(e.target.value)} data-testid="heatmap-until" className="border border-gray-300 px-2 py-1.5 text-sm mt-1 outline-none focus:border-pti-red" />
              </div>
              <button onClick={() => loadHeatmap()} data-testid="heatmap-apply" className="text-xs font-bold px-3 py-2 border border-pti-black hover:bg-pti-black hover:text-white transition-colors">Apply</button>
              <button onClick={() => { setHeatSince(""); setHeatUntil(""); loadHeatmap("", ""); }} data-testid="heatmap-clear" className="text-xs font-bold px-3 py-2 border border-gray-300 hover:border-pti-black transition-colors">Clear</button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <HeatTable title="Top pages by clicks" rows={heatmap.by_page} label="Page URL" />
              <HeatTable title="Placements by clicks" rows={heatmap.by_placement} label="Placement" />
            </div>
          </div>

          {/* Weekly reports */}
          <div className="border border-gray-200" data-testid="weekly-reports">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 font-bold text-sm">Weekly reports (auto-generated every 7 days)</div>
            {reports.length === 0 && <p className="p-4 text-gray-400 text-sm">No reports yet — one is generated automatically each week, or generate on demand above.</p>}
            {reports.map((r) => (
              <div key={r.id} className="px-4 py-2 flex items-center justify-between border-b border-gray-100 text-sm">
                <span>{r.filename} <span className="text-gray-400">· {r.rows} rows · {r.trigger} · {new Date(r.created_at).toLocaleString()}</span></span>
                <button onClick={() => downloadReport(r.id, r.filename)} data-testid={`report-dl-${r.id}`} className="flex items-center gap-1 text-xs font-bold text-pti-red"><Download size={12} /> Download</button>
              </div>
            ))}
          </div>

          {affiliate.length === 0 && <p className="text-gray-400">No affiliate campaigns yet. Click “Create Affiliate Campaign” to add one.</p>}
          {affiliate.map((ad) => (
            <div key={ad.id} className="border border-gray-200" data-testid={`affiliate-row-${ad.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3 min-w-0">
                  {ad.image ? <img src={ad.image} alt={ad.image_alt} className="h-10 w-auto max-w-[80px] object-contain border border-gray-200 bg-white" /> : <div className="h-10 w-14 flex items-center justify-center border border-gray-200 bg-white text-[9px] text-gray-400">HTML</div>}
                  <div className="min-w-0">
                    <div className="font-bold flex items-center gap-2">{ad.name} <StatusBadge status={ad.status} /></div>
                    <div className="text-xs text-gray-500 truncate">{ad.partner ? `${ad.partner} · ` : ""}{(ad.placements || []).length} placement(s){ad.max_impressions ? ` · cap ${ad.max_impressions}` : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center"><div className="font-display text-xl font-bold">{ad.impressions}</div><div className="text-[10px] uppercase text-gray-500">Impr.</div></div>
                  <div className="text-center"><div className="font-display text-xl font-bold">{ad.clicks}</div><div className="text-[10px] uppercase text-gray-500">Clicks</div></div>
                  <div className="text-center"><div className="font-display text-xl font-bold">{ad.ctr}%</div><div className="text-[10px] uppercase text-gray-500">CTR</div></div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-200">
                {ad.status !== "Active" && <ActionBtn onClick={() => setStatusAff(ad.id, "Active")} testid={`aff-activate-${ad.id}`} icon={<Play size={13} />} label="Activate" tone="green" />}
                {ad.status === "Active" && <ActionBtn onClick={() => setStatusAff(ad.id, "Paused")} testid={`aff-pause-${ad.id}`} icon={<Pause size={13} />} label="Pause" />}
                <ActionBtn onClick={() => openEdit(ad)} testid={`aff-edit-${ad.id}`} icon={<Pencil size={13} />} label="Edit" />
                <ActionBtn onClick={() => duplicateAff(ad.id)} testid={`aff-duplicate-${ad.id}`} icon={<Copy size={13} />} label="Duplicate" />
                <ActionBtn onClick={() => setStatusAff(ad.id, "Ended")} testid={`aff-end-${ad.id}`} icon={<Square size={12} />} label="End" />
                <ActionBtn onClick={() => viewLive(ad)} testid={`aff-viewlive-${ad.id}`} icon={<ExternalLink size={13} />} label="View live placement" />
                <ActionBtn onClick={() => deleteAff(ad.id)} testid={`aff-delete-${ad.id}`} icon={<Trash2 size={13} />} label="Delete" tone="red" />
                <span className="text-xs text-gray-400 ml-auto">Runs {ad.start_date || "now"} → {ad.end_date || "no end"} · weight {ad.weight || 1}</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                    <th className="p-2">Placement</th><th className="p-2">Category</th><th className="p-2">Page URL</th><th className="p-2 text-right">Impr.</th><th className="p-2 text-right">Clicks</th><th className="p-2 text-right">CTR</th>
                  </tr></thead>
                  <tbody>
                    {ad.breakdown.length === 0 && <tr><td colSpan={6} className="p-3 text-gray-400">No activity yet.</td></tr>}
                    {ad.breakdown.map((b, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="p-2 font-semibold">{b.placement || "—"}</td>
                        <td className="p-2">{b.category || "—"}</td>
                        <td className="p-2 text-gray-500">{b.page_url || "—"}</td>
                        <td className="p-2 text-right">{b.impressions}</td>
                        <td className="p-2 text-right">{b.clicks}</td>
                        <td className="p-2 text-right">{b.ctr}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <AffiliateCampaignForm open={showForm} onClose={() => setShowForm(false)} onSaved={loadAffiliate} placements={placements} initial={editing} />
        </div>
      )}

      {tab === "logs" && (
        <div className="mt-6 divide-y divide-gray-200 border border-gray-200">
          {logs.length === 0 && <p className="p-4 text-gray-400 text-sm">No moderation actions yet.</p>}
          {logs.map((l) => (
            <div key={l.id} className="p-3 text-sm flex justify-between"><span><strong>{l.action}</strong> · {l.kind} · {l.content_id?.slice(0, 8)}</span><span className="text-gray-400">{l.moderator} · {new Date(l.created_at).toLocaleString()}</span></div>
          ))}
        </div>
      )}

      {commentPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: "rgba(17,17,17,0.6)" }} onClick={() => setCommentPreview(null)} data-testid="comment-preview-modal">
          <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl border-t-4 border-pti-red" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[2px] text-pti-red">Comment Preview</div>
                <h3 className="font-display text-lg font-black">Awaiting review</h3>
              </div>
              <button onClick={() => setCommentPreview(null)} data-testid="comment-preview-close" className="text-gray-400 hover:text-pti-black"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                {commentPreview.author_avatar
                  ? <img src={commentPreview.author_avatar} alt={commentPreview.author_name} className="w-10 h-10 rounded-full object-cover" />
                  : <div className="w-10 h-10 rounded-full bg-pti-black text-white flex items-center justify-center font-bold text-sm">{(commentPreview.author_name || commentPreview.author_username || "?").slice(0, 1).toUpperCase()}</div>}
                <div className="min-w-0">
                  <div className="font-bold leading-tight">{commentPreview.author_name || commentPreview.author_username}</div>
                  <div className="text-xs text-gray-500">@{commentPreview.author_username} · {commentPreview.created_at ? new Date(commentPreview.created_at).toLocaleString() : ""}</div>
                </div>
              </div>

              <div className="border border-gray-200 bg-gray-50 p-4 text-[15px] leading-relaxed whitespace-pre-wrap break-words" data-testid="comment-preview-body">{commentPreview.body}</div>

              <div className="text-sm text-gray-600">
                <span className="font-bold text-pti-black">On:</span>{" "}
                {commentPreview.content_type === "article" && commentPreview.content_slug
                  ? <a href={`/article/${commentPreview.content_slug}`} target="_blank" rel="noopener noreferrer" className="text-pti-red font-semibold hover:underline inline-flex items-center gap-1" data-testid="comment-preview-context-link">{commentPreview.content_title} <ExternalLink size={13} /></a>
                  : <span>{commentPreview.content_title || `${commentPreview.content_type || "content"} (${(commentPreview.content_id || "").slice(0, 8)})`}</span>}
              </div>

              {commentPreview.spam_flag && (
                <div className="inline-flex items-center gap-1 bg-red-100 text-pti-red text-[11px] font-bold px-2 py-1 uppercase tracking-wide">⚠ Spam: {commentPreview.spam_reason}{typeof commentPreview.spam_score === "number" ? ` · score ${commentPreview.spam_score}` : ""}</div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
              <button onClick={() => { const id = commentPreview.id; setCommentPreview(null); moderate("comments", id, "Rejected"); }} data-testid="comment-preview-reject" className="text-sm font-bold px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50">Reject</button>
              <button onClick={() => { const id = commentPreview.id; setCommentPreview(null); moderate("comments", id, "Approved"); }} data-testid="comment-preview-approve" className="text-sm font-bold px-5 py-2 bg-pti-red text-white hover:bg-pti-black transition-colors">Approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Queue({ items, render, empty }) {
  return <div className="mt-6 space-y-3">{items.length === 0 ? <p className="text-gray-400">{empty}</p> : items.map(render)}</div>;
}
function Row({ title, sub, children, badge }) {
  return (
    <div className="border border-gray-200 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold line-clamp-1">{title}</div>
        <div className="text-xs text-gray-500">{sub}</div>
        {badge && <div className="mt-1 inline-flex items-center gap-1 bg-red-100 text-pti-red text-[11px] font-bold px-2 py-0.5 uppercase tracking-wide" data-testid="spam-badge">⚠ Spam: {badge}</div>}
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">{children}</div>
    </div>
  );
}
function Act({ onClick, label, primary, danger }) {
  return <button onClick={onClick} data-testid={`act-${label.replace(/\W/g, "").toLowerCase()}`}
    className={`text-xs font-bold px-3 py-1.5 border transition-colors ${primary ? "bg-pti-red text-white border-pti-red hover:bg-pti-redhover" : danger ? "border-red-300 text-red-600 hover:bg-red-50" : "border-gray-300 hover:border-pti-black"}`}>{label}</button>;
}

function StatusBadge({ status }) {
  const map = {
    Active: "bg-green-50 text-green-700 border-green-300",
    Paused: "bg-yellow-50 text-yellow-700 border-yellow-300",
    Draft: "bg-gray-100 text-gray-600 border-gray-300",
    Ended: "bg-red-50 text-pti-red border-red-300",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 border ${map[status] || map.Draft}`} data-testid="aff-status-badge">{status || "Draft"}</span>;
}

function ActionBtn({ onClick, testid, icon, label, tone }) {
  const cls = tone === "red" ? "border-red-300 text-pti-red hover:bg-red-50"
    : tone === "green" ? "border-green-300 text-green-700 hover:bg-green-50"
    : "border-gray-300 text-gray-700 hover:border-pti-black";
  return <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 border transition-colors ${cls}`}>{icon} {label}</button>;
}

function HeatTable({ title, rows, label }) {
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div className="border border-gray-200">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 font-bold text-sm">{title}</div>
      {rows.length === 0 && <p className="p-4 text-gray-400 text-sm">No data yet.</p>}
      {rows.slice(0, 10).map((r, i) => (
        <div key={i} className="px-4 py-2 border-b border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="truncate mr-2 font-medium">{r.key}</span>
            <span className="text-gray-500 shrink-0">{r.clicks} clicks · {r.impressions} impr · {r.ctr}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 mt-1"><div className="h-full bg-pti-red" style={{ width: `${(r.clicks / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
